import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDashboardStore } from '../../stores/useDashboardStore';
import { useCurrentStore } from '../../hooks/useCurrentStore';

const DashboardPage: React.FC = () => {
  const { t } = useTranslation(['pages', 'translation']);
  const { storeId } = useCurrentStore(); // 自動獲取當前用戶店鋪
  const {
    bookingStats,
    revenueStats,
    customerStats,
    serviceCount,
    recentBookings,
    isLoading,
    fetchDashboardData,
  } = useDashboardStore();

  const [isRefreshing, setIsRefreshing] = useState(false);

  // 載入儀表板數據（當店鋪資訊載入完成後）
  // 使用快取機制，只在快取過期或無數據時載入
  useEffect(() => {
    if (storeId) {
      fetchDashboardData(storeId, false); // 不強制刷新，使用快取
    }
  }, [storeId, fetchDashboardData]);

  const handleRefresh = async () => {
    if (!storeId) return;

    setIsRefreshing(true);
    await fetchDashboardData(storeId, true); // 強制刷新
    setIsRefreshing(false);
  };

  const getStatusBadge = (status: string) => {
    const statusMap: Record<string, { className: string }> = {
      PENDING: { className: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400' },
      CONFIRMED: { className: 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400' },
      COMPLETED: { className: 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400' },
      CANCELLED: { className: 'bg-gray-100 text-gray-800 dark:bg-slate-700 dark:text-gray-300' },
    };

    const statusInfo = statusMap[status] || { className: 'bg-gray-100 text-gray-800' };
    const label = t(`translation:bookingStatus.${status}` as any);

    return (
      <span className={`px-2 py-1 rounded text-xs font-medium ${statusInfo.className}`}>
        {label}
      </span>
    );
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('zh-TW', {
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // 只在首次無數據時顯示載入畫面
  if (!bookingStats && isLoading) {
    return (
      <div className="container mx-auto px-4 md:px-10 py-6 pb-32">
        <div className="text-center py-12 text-gray-500 dark:text-gray-400">
          {t('translation:common.loading')}
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 md:px-10 py-6 pb-32">
      {/* 標題和刷新按鈕 */}
      <div className="mb-4">
        <div className="flex items-baseline justify-between">
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
            {t('pages:admin.dashboard.title')}
          </h1>
          <button
            onClick={handleRefresh}
            disabled={isRefreshing}
            className={`px-4 py-2 rounded font-medium transition-colors ${
              isRefreshing
                ? 'bg-gray-300 dark:bg-slate-700 text-gray-500 cursor-not-allowed'
                : 'bg-brand-600 hover:bg-brand-700 text-white'
            }`}
          >
            {isRefreshing ? t('translation:common.refreshing') : t('translation:common.refresh')}
          </button>
        </div>
      </div>

      {/* 統計卡片 */}
      <div className="grid grid-cols-2 gap-4 mb-8">
        <div className="bg-white dark:bg-slate-800 p-4 rounded-lg shadow">
          <h3 className="text-sm text-gray-500 dark:text-gray-400 truncate">{t('pages:admin.dashboard.todayBookings')}</h3>
          <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1 overflow-hidden whitespace-nowrap">
            {bookingStats?.today || 0}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 truncate">
            {t('pages:admin.dashboard.thisWeek')}: {bookingStats?.week || 0}
          </p>
        </div>

        <div className="bg-white dark:bg-slate-800 p-4 rounded-lg shadow">
          <h3 className="text-sm text-gray-500 dark:text-gray-400 truncate">{t('pages:admin.dashboard.monthlyRevenue')}</h3>
          <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1 overflow-hidden whitespace-nowrap">
            ${Math.floor(revenueStats?.month || 0).toLocaleString()}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 truncate">
            {t('pages:admin.dashboard.todayRevenue')}: ${Math.floor(revenueStats?.today || 0).toLocaleString()}
          </p>
        </div>

        <div className="bg-white dark:bg-slate-800 p-4 rounded-lg shadow">
          <h3 className="text-sm text-gray-500 dark:text-gray-400 truncate">{t('pages:admin.dashboard.totalCustomers')}</h3>
          <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1 overflow-hidden whitespace-nowrap">
            {customerStats?.total || 0}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 truncate">
            {t('pages:admin.dashboard.newThisMonth')}: {customerStats?.newThisMonth || 0}
          </p>
        </div>

        <div className="bg-white dark:bg-slate-800 p-4 rounded-lg shadow">
          <h3 className="text-sm text-gray-500 dark:text-gray-400 truncate">{t('pages:admin.dashboard.serviceItems')}</h3>
          <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1 overflow-hidden whitespace-nowrap">
            {serviceCount}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 truncate">
            {t('pages:admin.dashboard.activeCustomers')}: {customerStats?.activeThisMonth || 0}{t('pages:admin.dashboard.position')}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* 預約狀態分布 */}
        <div className="bg-white dark:bg-slate-800 p-6 rounded-lg shadow">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
            {t('pages:admin.dashboard.bookingDistribution')}
          </h2>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                <span className="text-gray-700 dark:text-gray-300">{t('translation:bookingStatus.PENDING')}</span>
              </div>
              <span className="font-bold text-gray-900 dark:text-white">
                {bookingStats?.statusDistribution.PENDING || 0}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                <span className="text-gray-700 dark:text-gray-300">{t('translation:bookingStatus.CONFIRMED')}</span>
              </div>
              <span className="font-bold text-gray-900 dark:text-white">
                {bookingStats?.statusDistribution.CONFIRMED || 0}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                <span className="text-gray-700 dark:text-gray-300">{t('translation:bookingStatus.COMPLETED')}</span>
              </div>
              <span className="font-bold text-gray-900 dark:text-white">
                {bookingStats?.statusDistribution.COMPLETED || 0}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-slate-500 rounded-full"></div>
                <span className="text-gray-700 dark:text-gray-300">{t('translation:bookingStatus.CANCELLED')}</span>
              </div>
              <span className="font-bold text-gray-900 dark:text-white">
                {bookingStats?.statusDistribution.CANCELLED || 0}
              </span>
            </div>
          </div>
        </div>

        {/* 最近 7 天營收趨勢 */}
        <div className="bg-white dark:bg-slate-800 p-6 rounded-lg shadow">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
            {t('pages:admin.dashboard.revenueTrend')}
          </h2>
          <div className="space-y-2">
            {revenueStats?.trend.map((item) => {
              const maxRevenue = Math.max(...(revenueStats.trend.map(t => t.revenue) || [1]));
              const percentage = maxRevenue > 0 ? (item.revenue / maxRevenue) * 100 : 0;

              return (
                <div key={item.date}>
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="text-gray-600 dark:text-gray-400">
                      {new Date(item.date).toLocaleDateString('zh-TW', { month: '2-digit', day: '2-digit' })}
                    </span>
                    <span className="font-medium text-gray-900 dark:text-white">
                      ${Math.floor(item.revenue).toLocaleString()}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-slate-700 rounded-full h-2">
                    <div
                      className="bg-brand-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${percentage}%` }}
                    ></div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* 最近預約列表 */}
      <div className="mt-8 bg-white dark:bg-slate-800 p-4 md:p-6 rounded-lg shadow">
        <h2 className="text-lg md:text-xl font-bold text-gray-900 dark:text-white mb-4">
          {t('pages:admin.dashboard.recentBookings')}
        </h2>
        {recentBookings.length === 0 ? (
          <p className="text-gray-500 dark:text-gray-400 text-center py-4">
            {t('pages:admin.dashboard.noBookings')}
          </p>
        ) : (
          <>
            {/* 手機版卡片列表 */}
            <div className="md:hidden space-y-3">
              {recentBookings.map((booking) => (
                <div
                  key={booking.id}
                  className="bg-gray-50 dark:bg-slate-700/50 rounded-lg p-3 space-y-2"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-gray-900 dark:text-white truncate">
                        {booking.customer.name}
                      </p>
                      <p className="text-sm text-gray-600 dark:text-gray-400 truncate">
                        {booking.service.name}
                      </p>
                    </div>
                    <div className="flex-shrink-0">
                      {getStatusBadge(booking.status)}
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {formatDateTime(booking.date)}
                  </p>
                </div>
              ))}
            </div>

            {/* 桌面版表格 */}
            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead>
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      {t('pages:admin.dashboard.customer')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      {t('pages:admin.dashboard.service')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      {t('pages:admin.dashboard.time')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      {t('pages:admin.dashboard.status')}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {recentBookings.map((booking) => (
                    <tr key={booking.id}>
                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                        {booking.customer.name}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                        {booking.service.name}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                        {formatDateTime(booking.date)}
                      </td>
                      <td className="px-4 py-3">
                        {getStatusBadge(booking.status)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default DashboardPage;
